/* globals zoomSdk */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Divider,
  DragAndDrop,
  dragAndDropClasses,
} from '@zoom/react-ui';
import { IconPlusFill, IconCloseSmallV2, IconPlay } from '@zoom/icons-react';
import '../root/App.css';
import './Queue.css';
import SetTimer from './SetTimer';
import CustomTooltip from '../utilities/CustomTooltip/CustomTooltip';
import { TimeButtons } from './Buttons';
import { SoundButton } from '../utilities/MusicInTimer/SoundButton';
import { formatTime } from '../utils';
import { debounce } from 'lodash';
import { selectedTimerMusicOption } from '../utilities/MusicInTimer/reducer';
import { setMusicSelectedOption } from '../utilities/MusicInTimer/actions';

export default function Queue({
  countdownKey,
  countdownStatus,
  startTime,
  timerIsRunning,
  timerTime,
  setTimerTimeWithConstraint,
  stopwatchIsOn,
  openModal,
  setOpenModal,
  supportRunningContext,
  myTimerAudioToggleOn,
  dynamicIndicatorClientCheck,
  clientSupportNegativeAlarm,
  setTimerTime,
  startTimer,
  onContinueModal,
  isMacOS,
  setStartTime,
  setPresetTime,
  refsList,
  oldHostSupport,
  timerQueue,
  setTimerQueue,
  isTimerDISet,
  myDynamicIndicator,
  isStopwatchDISet,
  stopwatchVideoToggleOn,
  timerVideoToggleOn,
}) {
  const [modal, setModal] = useState({});
  const [tempTimerTime, setTempTimerTime] = useState(
    parseInt(window.localStorage.getItem('LastSetTime')) || 300
  );
  const [isScrollable, setIsScrollable] = useState(false);
  const queueRef = useRef(null);
  const [gotMessage, setGotMessage] = useState(false);
  const nextIdRef = useRef(0);
  const itemIdMap = useRef(new WeakMap());

  const getStableId = (item) => {
    if (!itemIdMap.current.has(item)) {
      nextIdRef.current += 1;
      itemIdMap.current.set(item, `queue-item-${nextIdRef.current}`);
    }
    return itemIdMap.current.get(item);
  };
  const selectedOption = useSelector(selectedTimerMusicOption);
  const [currentAlarmToneSetting, setCurrentAlarmToneSetting] = useState({
    name: selectedOption?.name,
    id: selectedOption?.id,
  });
  const dispatch = useDispatch();

  const hideControls =
    (isTimerDISet || isStopwatchDISet) && !myDynamicIndicator;

  const formatTimer = useCallback(
    (time) => {
      let hours = formatTime(time).hours;
      let minutes = formatTime(time).minutes;
      let seconds = formatTime(time).seconds;
      if (time === 0) {
        if (!timerIsRunning) {
          hours = '00';
          minutes = '00';
          seconds = '00';
        }
      }
      return { hours: hours, minutes: minutes, seconds: seconds };
    },
    [
      countdownKey,
      countdownStatus,
      startTime,
      timerIsRunning,
      timerTime,
      setTimerTimeWithConstraint,
      stopwatchIsOn,
      openModal,
      supportRunningContext,
      myTimerAudioToggleOn,
      dynamicIndicatorClientCheck,
      tempTimerTime,
    ]
  );

  const addToQueue = (time) => {
    setTimerQueue((queue) => [
      ...queue,
      [time, currentAlarmToneSetting?.name, currentAlarmToneSetting?.id],
    ]);
    setModal((prevState) => ({ ...prevState, open: false }));
  };

  const startTimerWithValue = (timerData) => {
    const [time, name, id] = timerData;
    if (stopwatchIsOn && stopwatchVideoToggleOn && timerVideoToggleOn) {
      setOpenModal({ open: true, time, timerData });
    } else {
      window.localStorage.setItem(
        'preferredMusic',
        JSON.stringify({ name: name, id: id })
      );
      dispatch(setMusicSelectedOption({ name: name, id: id }));

      setTimerQueue((queue) => queue.filter((item) => item !== timerData));
      setTimeout(() => {
        startTimer(time);
      }, 0);
    }
  };

  const editTimerQueue = (timerData) => {
    const [time, name, id] = timerData;
    setModal({ mode: 'edit', open: true, timerData });
    setTempTimerTime(time);
    setCurrentAlarmToneSetting({ name, id });
  };

  const updateTimerQueue = (targetTimerData) => {
    setModal({ open: false });
    setTimerQueue((prev) =>
      prev.map((item) =>
        item === targetTimerData
          ? [tempTimerTime, currentAlarmToneSetting?.name, currentAlarmToneSetting?.id]
          : item
      )
    );
  };

  useEffect(() => {
    const checkIfScrollable = () => {
      if (queueRef.current) {
        const { scrollHeight, clientHeight } = queueRef.current;
        setIsScrollable(scrollHeight > clientHeight);
      }
    };

    const debouncedCheck = debounce(() => {
      requestAnimationFrame(checkIfScrollable);
    }, 100);

    const resizeObserver = new ResizeObserver(debouncedCheck);

    if (queueRef.current) {
      resizeObserver.observe(queueRef.current);
    }

    return () => {
      if (resizeObserver && queueRef.current) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (isTimerDISet && !myDynamicIndicator) {
      if (timerQueue.length === 0 && gotMessage === false) {
        zoomSdk.sendMessage({
          payload: {
            newUserJoined: true,
          },
        });
      }
    }
  }, [isTimerDISet, myDynamicIndicator, timerQueue, gotMessage]);

  useEffect(() => {
    if (isTimerDISet && !myDynamicIndicator) {
      const onMessageHandler = (data) => {
        if (data.payload.timers) {
          setTimerQueue(data.payload.timers);
          setGotMessage(true);
        }
      };

      zoomSdk.addEventListener('onMessage', onMessageHandler);

      return () => {
        zoomSdk.removeEventListener('onMessage', onMessageHandler);
      };
    }
  }, [isTimerDISet, myDynamicIndicator]);

  useEffect(() => {
    if (isTimerDISet && myDynamicIndicator) {
      const sendQueueData = () => {
        zoomSdk.sendMessage({
          payload: {
            timers: timerQueue,
          },
        });
      };
      const onMessageHandler = (data) => {
        if (data.payload.newUserJoined === true) {
          sendQueueData();
        }
      };

      sendQueueData();
      zoomSdk.addEventListener('onMessage', onMessageHandler);

      return () => {
        zoomSdk.removeEventListener('onMessage', onMessageHandler);
      };
    }
  }, [myDynamicIndicator, myDynamicIndicator, timerQueue]);

  useEffect(() => {
    if (!isTimerDISet && !myDynamicIndicator && gotMessage) {
      setTimerQueue([]);
    }
  }, [isTimerDISet, myDynamicIndicator, gotMessage]);

  return (
    <div className='Home__timer_queue'>
      <div
        className={`Home__timer_queue_header${
          isScrollable ? ' scrollable' : ''
        }`}
      >
        <span id='Home__timer_queue_header_text'>Up next</span>
        {!hideControls && (
          <Button
            variant='icon'
            type='tertiary'
            onClick={() => setModal({ mode: 'add', open: true })}
          >
            <IconPlusFill />
          </Button>
        )}
      </div>
      <div
        className={`Home__timer_queue_container${
          supportRunningContext ? '' : ' full'
        }`}
        ref={queueRef}
      >
        <DragAndDrop
          columns={[
            {
              droppableId: 'timer-queue',
              dataSource: timerQueue.map((timerData) => ({
                id: getStableId(timerData),
                timerData,
              })),
            },
          ]}
          onChange={(newColumns) => {
            const reordered = newColumns[0].dataSource.map(
              (item) => item.timerData
            );
            setTimerQueue(() => reordered);
          }}
          hidePlaceholder
          iconPosition='left'
          sx={{
            [`&.${dragAndDropClasses.root}`]: {
              background: 'transparent',
              padding: 0,
            },
            [`.${dragAndDropClasses.item}`]: {
              background: 'transparent !important',
              border: 'none !important',
              borderRadius: 0,
              marginBottom: 0,
              padding: 0,
            },
            [`.${dragAndDropClasses.default}`]: {
              background: 'transparent !important',
              border: 'none !important',
            },
            [`.${dragAndDropClasses.active}`]: {
              background: 'rgba(255,255,255,0.05) !important',
              border: 'none !important',
              borderRadius: '8px',
            },
            [`.${dragAndDropClasses.handler}`]: {
              color: '#96a1aa !important',
              cursor: hideControls ? 'default' : 'grab',
              opacity: hideControls ? 0 : 0.6,
              '&:hover': {
                opacity: hideControls ? 0 : 1,
              },
            },
          }}
          render={(data, { dragProvided, dragSnap }) => {
            const { timerData } = data;
            const [time] = timerData;
            return (
              <div key={data.id} ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                <div className='Home__timer_queue_item'>
                  <div
                    className='Home__timer_queue_drag_handle'
                    {...dragProvided.dragHandleProps}
                  >
                    {!hideControls && (
                      <span className='drag-handle-icon'>⠿</span>
                    )}
                  </div>
                  <div
                    className='Home__timer_queue_time'
                    onClick={() => editTimerQueue(timerData)}
                  >
                    {Math.abs(formatTimer(time).hours) > 0 && (
                      <>
                        <span>{formatTimer(time).hours}</span>
                        <span>:</span>
                      </>
                    )}
                    <span>{formatTimer(time).minutes} </span>
                    <span>:</span>
                    <span>{formatTimer(time).seconds}</span>
                  </div>
                  {!hideControls && (
                    <div className='Countdown__control_container'>
                      <div className='Countdown__control_button'>
                        <Button
                          variant='icon'
                          type='tertiary'
                          aria-label='Start'
                          onClick={() => startTimerWithValue(timerData)}
                        >
                          <IconPlay />
                        </Button>
                      </div>
                      <Button
                        variant='icon'
                        type='tertiary'
                        data-tooltip-id='timer-cancelbutton-tooltip'
                        data-tooltip-delay-show={300}
                        onClick={() =>
                          setTimerQueue((queue) =>
                            queue.filter((item) => item !== timerData)
                          )
                        }
                      >
                        <IconCloseSmallV2 />
                      </Button>
                    </div>
                  )}
                </div>
                {timerQueue.length >= 1 && <Divider />}
              </div>
            );
          }}
        />
      </div>
      {modal.open && (
        <div className='Home__set_timer_modal'>
          <Button
            id='close'
            variant='icon'
            type='tertiary'
            aria-label='close'
            onClick={() =>
              setModal((prevState) => ({ ...prevState, open: false }))
            }
          >
            <IconCloseSmallV2 />
          </Button>
          <div className='Home__set_timer_container_in_modal'>
            <div
              data-testid='timerContainer'
              className={`Home__timer_input_container${
                (!clientSupportNegativeAlarm || !supportRunningContext) &&
                timerTime === 0 &&
                timerIsRunning
                  ? ' fade'
                  : ''
              }`}
            >
              <SetTimer
                hours={formatTimer(tempTimerTime).hours}
                minutes={formatTimer(tempTimerTime).minutes}
                seconds={formatTimer(tempTimerTime).seconds}
                timerTime={timerTime}
                setTimerTime={setTimerTime}
                setTimerTimeWithConstraint={setTimerTimeWithConstraint}
                startTimerOnEnterPress={startTimer}
                stopwatchIsOn={stopwatchIsOn}
                openModal={openModal}
                onContinueModal={onContinueModal}
                supportRunningContext={supportRunningContext}
                timerVideoToggleOn={myTimerAudioToggleOn}
                dynamicIndicatorClientCheck={dynamicIndicatorClientCheck}
                tempTimerTime={tempTimerTime}
                setTempTimerTime={setTempTimerTime}
              />
            </div>
            <div className='Home__timer_controls'>
              <div className='Home__start_button'>
                <Button
                  size='large'
                  fullWidth={true}
                  id={
                    tempTimerTime === 0
                      ? 'input_start_button_disabled'
                      : 'input_start_button'
                  }
                  data-tooltip-id='timer-startbutton-tooltip'
                  data-tooltip-delay-show={300}
                  disabled={tempTimerTime === 0}
                  onClick={() => {
                    modal.mode === 'add'
                      ? addToQueue(tempTimerTime)
                      : updateTimerQueue(modal.timerData);
                  }}
                >
                  Save
                </Button>
                <CustomTooltip
                  text='Keyboard shortcut'
                  keyName={isMacOS ? 'return' : 'enter'}
                  id='timer-startbutton-tooltip'
                />
              </div>
              <TimeButtons
                setStartTime={setStartTime}
                setPresetTime={setPresetTime}
                setTimerTimeWithConstraint={setTimerTimeWithConstraint}
                setTempTimerTime={setTempTimerTime}
                mode='add'
                addToQueue={addToQueue}
              />
              <SoundButton
                refs={refsList}
                source='Queue'
                currentAlarmToneSetting={currentAlarmToneSetting}
                setCurrentAlarmToneSetting={setCurrentAlarmToneSetting}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
